import { memo } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  CheckCheck,
  Copy,
  File as FileIcon,
  FileText,
  ListChecks,
  Music,
  Pencil,
  Play,
  Reply,
  Trash2,
} from 'lucide-react';
import { NoteIcon } from '@/shared/config';
import type { TicketMessage } from '@/entities/ticket';
import type { MessageMailInfo } from '@/shared/api';
import { EmailIcon, ReassignIcon } from '@/shared/config';
import { resolveApiUrl } from '@/shared/lib/config';
import { cn } from '@/shared/lib/cn';
import {
  Avatar,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  Tooltip,
} from '@/shared/ui';
import {
  attachmentKind,
  groupAttachments,
  type AttachmentKind,
} from '../../lib/attachments';
import { attachmentKey, formatBytes, type DeleteRequest } from './chat-lib';
import type { TicketReader } from './useChatSocket';
import { RichText } from '@/features/wiki';
import { mentionsUser } from '@/shared/lib/mentions';
import { useAuthStore } from '@/features/auth';
import styles from '../TicketChat.module.css';

export interface ChatMessageProps {
  msg: TicketMessage;
  currentUserId: string;
  showAuthor: boolean;
  mutable: boolean;
  selectMode: boolean;
  selected: boolean;
  selectedAttachments: Set<string>;
  isEditing: boolean;
  isFlashing: boolean;
  readers: TicketReader[];
  onShowReaders: (readers: TicketReader[]) => void;
  quoteInfo: { author: string; text: string; thumbUrl: string | null } | null;
  onReply: (messageId: string, attachmentId?: string) => void;
  onEdit: (m: TicketMessage) => void;
  onEnterSelect: (m: TicketMessage) => void;
  onToggleSelected: (id: string) => void;
  onToggleSelectedAttachment: (messageId: string, attachmentId: string) => void;
  onRequestDelete: (req: DeleteRequest) => void;
  onCopyText: (m: TicketMessage) => void;
  onOpenViewer: (url: string) => void;
  onQuoteClick: (messageId: string) => void;
  onImageLoad: () => void;
  noteBadge?: boolean;
  mailMeta?: MessageMailInfo;
  canEmail?: boolean;
  canMove?: boolean;
  onMailAction?: (action: MailAction, messageId: string) => void;
}

export type MailAction = 'email' | 'original' | 'move';

const AUTH_HINT: Record<string, string> = {
  pass: 'адрес отправителя подтверждён',
  fail: 'проверка SPF/DKIM не пройдена — адрес мог быть подделан',
  none: 'сервер не сообщил, подтверждён ли адрес',
};

function outboundSummary(meta: MessageMailInfo | undefined): { tone: 'ok' | 'wait' | 'bad'; text: string } | null {
  const last = meta?.outbound[meta.outbound.length - 1];
  if (!last) return null;
  const to = [...last.to, ...last.cc].join(', ');
  if (last.status === 'sent') return { tone: 'ok', text: `Отправлено письмом: ${to}` };
  if (last.status === 'queued' || last.status === 'sending') return { tone: 'wait', text: `Письмо отправляется: ${to}` };
  if (last.status === 'bounced') return { tone: 'bad', text: `Письмо не доставлено: ${to}` };
  return { tone: 'bad', text: `Письмо не ушло${last.error ? `: ${last.error}` : ''}` };
}

const fileRowIcon = (kind: AttachmentKind) => {
  if (kind === 'audio') return <Music size={18} />;
  if (kind === 'document') return <FileText size={18} />;
  return <FileIcon size={18} />;
};

function ChatMessageBase({
  msg: m,
  currentUserId,
  showAuthor,
  mutable,
  selectMode,
  selected,
  selectedAttachments,
  isEditing,
  isFlashing,
  readers,
  onShowReaders,
  quoteInfo,
  onReply,
  onEdit,
  onEnterSelect,
  onToggleSelected,
  onToggleSelectedAttachment,
  onRequestDelete,
  onCopyText,
  onOpenViewer,
  onQuoteClick,
  onImageLoad,
  noteBadge = true,
  mailMeta,
  canEmail = false,
  canMove = false,
  onMailAction,
}: ChatMessageProps) {
  const inbound = mailMeta?.inbound;
  const outbound = outboundSummary(mailMeta);
  const own = m.authorId === currentUserId;
  const canViewProfiles = useAuthStore((s) => s.hasPermission('view_user_profiles'));
  const authorHref = m.author && (canViewProfiles || m.author.publicProfile) ? `/users/${m.author.id}` : null;
  const mentionsMe = !own && mentionsUser(m.body, currentUserId);

  const wrapAttachmentMenu = (attachmentId: string, trigger: React.ReactElement, tooltip?: string) => {
    const menuTrigger = (
      <ContextMenuTrigger
        render={trigger}
        onContextMenu={(e: React.MouseEvent) => e.stopPropagation()}
      />
    );
    return (
    <ContextMenu key={attachmentId}>
      { }
      {tooltip ? <Tooltip content={tooltip}>{menuTrigger}</Tooltip> : menuTrigger}
      <ContextMenuContent>
        <ContextMenuItem onClick={() => onReply(m.id, attachmentId)}>
          <Reply size={14} aria-hidden /> Ответить на файл
        </ContextMenuItem>
        {mutable && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              variant="danger"
              onClick={() => onRequestDelete({ kind: 'attachment', messageId: m.id, attachmentId })}
            >
              <Trash2 size={14} aria-hidden /> Удалить файл
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
    );
  };

  const renderAttachments = () => {
    const attachments = m.attachments ?? [];
    if (attachments.length === 0) return null;
    const { media, audio, documents, files } = groupAttachments(attachments);
    const rows = [...audio, ...documents, ...files];
    const selectable = selectMode && mutable;
    const interactiveMenu = !selectMode;
    return (
      <>
        {media.length > 0 && (
          <div className={styles.imageGrid} data-count={Math.min(media.length, 6)}>
            {media.map((a) => {
              const url = resolveApiUrl(a.url);
              const thumbUrl = a.thumbUrl ? resolveApiUrl(a.thumbUrl) : url;
              const kind = attachmentKind(a.mimeType, a.fileName);
              const attSelected = selectedAttachments.has(attachmentKey(m.id, a.id));
              const item = (
                <button
                  key={a.id}
                  type="button"
                  className={cn(styles.imageItem, attSelected && styles.attachmentSelected)}
                  onClick={
                    selectable
                      ? () => onToggleSelectedAttachment(m.id, a.id)
                      : () => onOpenViewer(url)
                  }
                  title={a.fileName}
                >
                  {kind === 'video' ? (
                    <>
                      <video src={url} muted preload="metadata" playsInline />
                      <span className={styles.playOverlay} aria-hidden>
                        <Play size={20} fill="currentColor" />
                      </span>
                    </>
                  ) : (
                    <>
                      <img src={thumbUrl} alt={a.fileName} loading="lazy" onLoad={onImageLoad} />
                      {kind === 'gif' && <span className={styles.mediaBadge}>GIF</span>}
                    </>
                  )}
                </button>
              );
              return interactiveMenu ? wrapAttachmentMenu(a.id, item) : item;
            })}
          </div>
        )}
        {rows.map((a) => {
          const kind = attachmentKind(a.mimeType, a.fileName);
          const attSelected = selectedAttachments.has(attachmentKey(m.id, a.id));
          const item = (
            <a
              key={a.id}
              className={cn(styles.fileRow, attSelected && styles.attachmentSelected)}
              href={resolveApiUrl(a.url)}
              target="_blank"
              rel="noreferrer"
              download={a.fileName}
              onClick={
                selectable
                  ? (e) => {
                      e.preventDefault();
                      onToggleSelectedAttachment(m.id, a.id);
                    }
                  : undefined
              }
            >
              <span className={styles.fileIcon}>{fileRowIcon(kind)}</span>
              <span className={styles.fileInfo}>
                <div className={styles.fileName}>{a.fileName}</div>
                <div className={styles.fileSize}>{formatBytes(a.sizeBytes)}</div>
              </span>
            </a>
          );
          return interactiveMenu ? wrapAttachmentMenu(a.id, item, 'Скачать') : item;
        })}
      </>
    );
  };

  const bubbleClass = cn(
    styles.bubble,
    own && styles.own,
    m.internal && noteBadge && styles.internal,
    selectMode && mutable && styles.selectable,
    selected && styles.selected,
    mentionsMe && styles.mentioned,
    isEditing && styles.editingNow,
    isFlashing && styles.flash,
  );

  const bubbleContent = (
    <>
      {m.internal && noteBadge && (
        <Tooltip content="Видна только сотрудникам, заявитель её не получит">
          <div className={styles.internalBadge}>
            <NoteIcon size={12} aria-hidden /> Внутренняя заметка
          </div>
        </Tooltip>
      )}
      {inbound && (
        <Tooltip
          content={`Пришло письмом с ${inbound.from ?? 'неизвестного адреса'}${
            inbound.sentAt ? ` · ${new Date(inbound.sentAt).toLocaleString('ru')}` : ''
          }; ${AUTH_HINT[inbound.authResult] ?? ''}`}
        >
          <div className={cn(styles.emailBadge, inbound.authResult === 'fail' && styles.emailBadgeDanger)}>
            <EmailIcon size={12} aria-hidden /> Из письма · {inbound.from}
          </div>
        </Tooltip>
      )}
      {showAuthor &&
        (authorHref ? (
          <Link to={authorHref} className={cn(styles.author, styles.authorLink)} onClick={(e) => e.stopPropagation()}>
            {m.author?.displayName}
          </Link>
        ) : (
          <div className={styles.author}>{m.author?.displayName ?? 'Собеседник'}</div>
        ))}
      { }
      {m.replyToMessageId && quoteInfo && (
        <button
          type="button"
          className={styles.quote}
          onClick={(e) => {
            e.stopPropagation();
            onQuoteClick(m.replyToMessageId!);
          }}
        >
          {quoteInfo.thumbUrl && <img className={styles.quoteThumb} src={quoteInfo.thumbUrl} alt="" />}
          <span className={styles.quoteBody}>
            <span className={styles.quoteAuthor}>{quoteInfo.author}</span>
            {quoteInfo.text && <span className={styles.quoteText}>{quoteInfo.text}</span>}
          </span>
        </button>
      )}
      {renderAttachments()}
      {m.body && (
        <RichText
          text={m.body}
          className={styles.bubbleText}
          mentions={m.mentions}
          currentUserId={currentUserId}
        />
      )}
      <span className={styles.meta}>
        {m.editedAt && <span title={new Date(m.editedAt).toLocaleString('ru')}>изменено </span>}
        {outbound && (
          <Tooltip content={outbound.text}>
            <span
              className={cn(styles.emailMark, outbound.tone === 'bad' && styles.emailMarkBad)}
              aria-label={outbound.text}
            >
              <EmailIcon size={12} aria-hidden />
            </span>
          </Tooltip>
        )}
        {new Date(m.createdAt).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
        { }
        {m.authorId === currentUserId && (
          <span
            className={cn(styles.readMark, readers.length > 0 && styles.readMarkDone)}
            title={readers.length > 0 ? `Прочитали: ${readers.length}` : 'Доставлено'}
            aria-label={readers.length > 0 ? `Прочитали: ${readers.length}` : 'Доставлено'}
          >
            {readers.length > 0 ? (
              <CheckCheck size={13} aria-hidden />
            ) : (
              <Check size={13} aria-hidden />
            )}
          </span>
        )}
      </span>
    </>
  );

  return !selectMode ? (
    <ContextMenu>
      <ContextMenuTrigger className={bubbleClass} data-mid={m.id}>
        {bubbleContent}
      </ContextMenuTrigger>
      <ContextMenuContent>
        { }
        {own && readers.length > 0 && (
          <>
            <ContextMenuItem onClick={() => onShowReaders(readers)}>
              <span className={styles.readersMini} aria-hidden>
                {readers.slice(0, 3).map((r) => (
                  <Avatar
                    key={r.userId}
                    className={styles.readersMiniAvatar}
                    name={r.displayName}
                    seed={r.userId}
                    src={r.avatarUrl}
                  />
                ))}
              </span>
              Видели {readers.length}
            </ContextMenuItem>
            <ContextMenuSeparator />
          </>
        )}
        <ContextMenuItem onClick={() => onReply(m.id)}>
          <Reply size={14} aria-hidden /> Ответить
        </ContextMenuItem>
        {m.body.trim() && (
          <ContextMenuItem onClick={() => onCopyText(m)}>
            <Copy size={14} aria-hidden /> Скопировать текст
          </ContextMenuItem>
        )}
        {inbound && onMailAction && (
          <ContextMenuItem onClick={() => onMailAction('original', m.id)}>
            <EmailIcon size={14} aria-hidden /> Оригинал письма
          </ContextMenuItem>
        )}
        {canEmail && !inbound && !m.internal && onMailAction && (
          <ContextMenuItem onClick={() => onMailAction('email', m.id)}>
            <EmailIcon size={14} aria-hidden />
            {mailMeta?.outbound.length ? ' Отправить письмом ещё раз' : ' Отправить письмом'}
          </ContextMenuItem>
        )}
        {canMove && onMailAction && (
          <ContextMenuItem onClick={() => onMailAction('move', m.id)}>
            <ReassignIcon size={14} aria-hidden /> Перенести в другую заявку…
          </ContextMenuItem>
        )}
        {mutable && (
          <>
            <ContextMenuItem onClick={() => onEdit(m)}>
              <Pencil size={14} aria-hidden /> Редактировать
            </ContextMenuItem>
            <ContextMenuItem onClick={() => onEnterSelect(m)}>
              <ListChecks size={14} aria-hidden /> Выбрать
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem
              variant="danger"
              onClick={() => onRequestDelete({ kind: 'bulk', messageIds: [m.id], attachments: [] })}
            >
              <Trash2 size={14} aria-hidden /> Удалить
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  ) : (
    <div
      className={bubbleClass}
      data-mid={m.id}
      onClick={mutable ? () => onToggleSelected(m.id) : undefined}
    >
      {bubbleContent}
    </div>
  );
}

export const ChatMessage = memo(ChatMessageBase);
